import React, { useState } from 'react';
import { useQuery } from '@apollo/client';

import useSession from 'core/hooks/use-session';
import { canSearchAgencyReports } from 'core/helpers/agency-reports';
import { Button } from 'core/components/button';
import { Label } from 'core/components/label';
import { Loading } from 'core/components/loading';
import AgencyStemSearch from './agency-stem-search';
import { LIST_AGENCY_REPORTS } from './agency-reports.queries';
import { formatReportMonth, openAgencyReport } from './agency-reports.helpers';
import useStyles from './agency-reports.styles';

/**
 * Agency Reports page (#36219): the monthly Agency Performance Report PDFs for one agency stem.
 * Internal users pick the agency (any stem); agency users get their own stem straight away — the
 * lambda derives it from their affinity code and ignores the argument, so the page sends null.
 * Reached from the account menu; gated there and on the route by core/helpers/agency-reports.
 */
const AgencyReports = () => {
  const classes = useStyles();
  const session = useSession();
  const canSearch = canSearchAgencyReports(session);
  const [affinityStem, setAffinityStem] = useState(null);
  const [openError, setOpenError] = useState(null);

  // Reports are listed fresh each time (no cache): a new month's drop should show up on reload.
  const { data, loading, error } = useQuery(LIST_AGENCY_REPORTS, {
    variables: { affinityStem },
    fetchPolicy: 'no-cache',
    skip: canSearch && !affinityStem
  });

  const result = data?.listAgencyReports;
  const listed = result?.success ? result : null;
  const errorMessage = error?.message || (result && !result.success ? result.error : null);

  const handleSelect = (stem) => {
    setOpenError(null);
    setAffinityStem(stem);
  };

  const handleOpen = async (key) => {
    setOpenError(null);
    try {
      await openAgencyReport(key);
    } catch (openFailure) {
      setOpenError(openFailure.message);
    }
  };

  return (
    <div className={classes.container}>
      <Label type="title" className={classes.title}>
        Agency Reports
      </Label>
      <Label type="infoValue" className={classes.subtitle}>
        {canSearch
          ? 'Monthly Agency Performance Reports. Search for an agency by name, affinity code or stem.'
          : 'Monthly Agency Performance Reports for your agency.'}
      </Label>
      {canSearch && <AgencyStemSearch onSelect={handleSelect} />}
      {loading && <Loading noBackground />}
      {!loading && errorMessage && <Label className={classes.error}>{errorMessage}</Label>}
      {!loading && listed && (
        <>
          <Label type="subtitle" className={classes.listTitle}>
            Reports for {listed.affinityStem}
          </Label>
          {listed.reports.length === 0 ? (
            <Label type="infoValue" className={classes.empty}>
              No reports yet for {listed.affinityStem}.
            </Label>
          ) : (
            <div className={classes.list}>
              {listed.reports.map((report) => (
                <div key={report.key} className={classes.row}>
                  <div className={classes.rowText}>
                    <Label type="infoValueBold">{formatReportMonth(report.reportMonth)}</Label>
                    <Label type="infoLabel">{report.reportType}</Label>
                  </div>
                  <Button color="secondary" variant="contained" onClick={() => handleOpen(report.key)}>
                    Open
                  </Button>
                </div>
              ))}
            </div>
          )}
        </>
      )}
      {openError && <Label className={classes.error}>{openError}</Label>}
    </div>
  );
};

export default AgencyReports;
